import React, { useEffect, useMemo, useState } from "react";
import { Checkbox, FormControlLabel, InputLabel, TextField } from "@mui/material";

const CustomerAddress = ({
  customerForm,
  handleChange,
  validationErrors,
  setDeliveryAddress,
  customerId = "",
  customerName = "", 
}) => {
  const [isDeliveryAddressSame, setIsDeliveryAddressSame] = useState(false);

  const updateField = (name, value) => {
    handleChange({ target: { name, value } });
  };

  const billing = useMemo(
    () => ({
      street: customerForm?.billingstreetaddress ?? "",
      city: customerForm?.billingcity ?? "",
      postal: customerForm?.billingpostalcode ?? "",
      state: customerForm?.billingstateprovince ?? "",
    }),
    [
      customerForm?.billingstreetaddress,
      customerForm?.billingcity,
      customerForm?.billingpostalcode,
      customerForm?.billingstateprovince,
    ]
  );

  // When checkbox turns ON, copy billing -> delivery immediately
  useEffect(() => {
    if (!isDeliveryAddressSame) return;

    updateField("delstreetaddress", billing.street);
    updateField("delcity", billing.city);
    updateField("delpostalcode", billing.postal);
    updateField("delstate", billing.state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeliveryAddressSame]);

  // While checkbox is ON, keep delivery synced as billing changes
  useEffect(() => {
    if (!isDeliveryAddressSame) return;

    updateField("delstreetaddress", billing.street);
    updateField("delcity", billing.city);
    updateField("delpostalcode", billing.postal);
    updateField("delstate", billing.state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billing.street, billing.city, billing.postal, billing.state, isDeliveryAddressSame]);

  const onToggleSameAsBilling = () => {
    setIsDeliveryAddressSame((prev) => {
      const next = !prev;

      if (typeof setDeliveryAddress === "function") {
        setDeliveryAddress(next);
      }
      return next;
    });
  };

  return (
    <div className="customer-add">
      {/* Billing */}
      <div className="d-flex flex-column">
        <h6
          className="p-2 col-2 mt-0 text-center"
          style={{ backgroundColor: "var(--primary-light-green-color)" }}
        >
          Billing Address
        </h6>

        <div className="d-flex ms-3">
          <div className="me-4 w-100">
            <InputLabel>Street Address</InputLabel>
            <TextField
              name="billingstreetaddress"
              value={customerForm?.billingstreetaddress ?? ""}
              sx={{ width: "100%" }}
              onChange={(e) => handleChange(e)}
              error={Boolean(validationErrors?.billingstreetaddress)}
              helperText={validationErrors?.billingstreetaddress}
            />
          </div>
        </div>

        <div className="d-flex ms-3 mt-2">
          <div className="col-4">
            <InputLabel>City</InputLabel>
            <TextField
              name="billingcity"
              value={customerForm?.billingcity ?? ""}
              onChange={(e) => handleChange(e)}
              sx={{ width: "91%" }}
              error={Boolean(validationErrors?.billingcity)}
              helperText={validationErrors?.billingcity}
            />
          </div>

          <div className="col-4">
            <InputLabel>Postal Code</InputLabel>
            <TextField
              name="billingpostalcode"
              value={customerForm?.billingpostalcode ?? ""}
              onChange={(e) => handleChange(e)}
              sx={{ width: "91%" }}
              error={Boolean(validationErrors?.billingpostalcode)}
              helperText={validationErrors?.billingpostalcode}
            />
          </div>

          <div className="col-4">
            <InputLabel>State</InputLabel>
            <TextField
              name="billingstateprovince"
              value={customerForm?.billingstateprovince ?? ""}
              onChange={(e) => handleChange(e)}
              sx={{ width: "91%" }}
              error={Boolean(validationErrors?.billingstateprovince)}
              helperText={validationErrors?.billingstateprovince}
            />
          </div>
        </div>
      </div>

      {/* Delivery */}
      <div className="d-flex flex-column mt-4">
        <div className="d-flex">
          <h6
            className="p-2 col-3 text-center"
            style={{ backgroundColor: "var(--primary-light-green-color)" }}
          >
            Delivery Address
          </h6>

          <div className="ms-auto pe-2">
            <FormControlLabel
              control={
                <Checkbox
                  checked={isDeliveryAddressSame}
                  onChange={onToggleSameAsBilling}
                  name="isSame"
                />
              }
              label="Same as Billing Address"
            />
          </div>
        </div>

        <div className="d-flex ms-3">
          <div className="me-4 w-100">
            <InputLabel>Street Address</InputLabel>
            <TextField
              name="delstreetaddress"
              value={customerForm?.delstreetaddress ?? ""}
              sx={{ width: "100%" }}
              onChange={(e) => handleChange(e)}
              disabled={isDeliveryAddressSame}
              error={Boolean(validationErrors?.delstreetaddress)}
              helperText={validationErrors?.delstreetaddress}
            />
          </div>
        </div>

        <div className="d-flex ms-3 mt-2">
          <div className="col-4">
            <InputLabel>City</InputLabel>
            <TextField
              name="delcity"
              value={customerForm?.delcity ?? ""}
              onChange={(e) => handleChange(e)}
              sx={{ width: "91%" }}
              disabled={isDeliveryAddressSame}
              error={Boolean(validationErrors?.delcity)}
              helperText={validationErrors?.delcity}
            />
          </div>

          <div className="col-4">
            <InputLabel>Postal Code</InputLabel>
            <TextField
              name="delpostalcode"
              value={customerForm?.delpostalcode ?? ""}
              onChange={(e) => handleChange(e)}
              sx={{ width: "91%" }}
              disabled={isDeliveryAddressSame}
              error={Boolean(validationErrors?.delpostalcode)}
              helperText={validationErrors?.delpostalcode}
            />
          </div>

          <div className="col-4">
            <InputLabel>State</InputLabel>
            <TextField
              name="delstate"
              value={customerForm?.delstate ?? ""}
              onChange={(e) => handleChange(e)}
              sx={{ width: "91%" }}
              disabled={isDeliveryAddressSame}
              error={Boolean(validationErrors?.delstate)}
              helperText={validationErrors?.delstate}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerAddress;
